import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './JobsPage.css';
import { getJobList, postJobStatus } from './Service/RecruitmentService';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { TostMsg } from '../../../../App';
import { GetMasterTypeData } from '../../../../Services/Global-Services/GlobalServices';
import ERROR_LIST from '../../../../Services/Global-Services/Hadle_Errors';
import { Paginator } from 'primereact/paginator';

const NA = 'N/A';

const safe = (val) => {
  if (val === null || val === undefined) return NA;
  if (typeof val === 'string' && val.trim() === '') return NA;
  return val;
};

const formatDate = (dateStr) => {
  if (!dateStr) return NA;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return NA;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return 'NA';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAgeClass = (days) => {
  if (days === null || days === undefined || Number.isNaN(days)) return 'age-new';
  if (days <= 15) return 'age-new';
  if (days <= 30) return 'age-mid';
  if (days <= 45) return 'age-warn';
  return 'age-old';
};

const statusKey = (statusName) => {
  if (!statusName) return 'open';
  const n = statusName.toLowerCase();
  if (n.includes('hold')) return 'hold';
  if (n.includes('closed')) return 'closed';
  return 'open';
};

const workModeIcon = (mode) => {
  if (!mode) return 'fa-solid fa-building';
  const n = mode.toLowerCase();
  if (n.includes('remote')) return 'fa-solid fa-wifi';
  if (n.includes('hybrid')) return 'fa-solid fa-house-laptop';
  return 'fa-solid fa-building';
};

const STATUS_TO_CARD_CLASS = { open: 'open', hold: 'hold', closed: 'closed' };
const STATUS_TO_BADGE_CLASS = { open: 'sb-open', hold: 'sb-hold', closed: 'sb-closed' };
const STATUS_TO_PILL_CLASS = { open: 'sopen', hold: 'shold', closed: 'sclosed' };

const naStyle = { color: 'var(--muted, #12161e)', fontStyle: 'italic' };

// Sort modal: first a field dropdown, then Ascending / Descending buttons.
// Keys are the API "SortBy" values.
const SORT_FIELDS = {
  PublishedOn: 'Published date',
  TotalCandidates: 'Total candidates',
  Title: 'Job title',
  JobAgeInDays: 'Job age',
};

const DEFAULT_SORT = { field: 'PublishedOn', order: 'DESC' };
const DEFAULT_PUBLISH = [{ Id: 0, Name: 'All' }, { Id: 1, Name: 'Published' }, { Id: 2, Name: 'Draft' }];
// Hold / Close / Reopen target the job status whose Name resolves (via
// statusKey) to one of these keys, looked up against talentstatusList.
const TARGET_STATUS_KEY = { holdModal: 'hold', closeJobModal: 'closed', reopenModal: 'open' };

// "More filters" panel defaults (declared outside the component so it is not
// recreated on every render).

const EMPTY_MORE = {
  location: '',
  employmentType: '',
  workMode: '',
  experienceRange: '',
  postedAfter: '',
};

const JobsPage = () => {
  const toster = useContext(TostMsg);
  const userDetails = JSON.parse(localStorage.getItem('User') || '{}');
  const searchDebounceRef = useRef(null);
  const requestIdRef = useRef(0);

  // ---- view state -------------------------------------------------------
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [showSummary, setShowSummary] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // ---- list / summary data ----------------------------------------------
  const [jobList, setJobList] = useState([]);
  const [summary, setSummary] = useState({ TotalJobs: 0, OpenJobs: 0, OnHoldJobs: 0, ClosedJobs: 0 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // ---- pagination ---------------------------------------------------------
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // ---- filters ------------------------------------------------------------
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // JobStatusId, '' = All
  const [department, setDepartment] = useState('');
  const [publishStatus, setPublishStatus] = useState('0'); // PublishStatusId, '' = All

  // ---- sort (applied + draft used by the modal) ---------------------------
  const [sortField, setSortField] = useState(DEFAULT_SORT.field);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT.order);
  const [sortDraftField, setSortDraftField] = useState(DEFAULT_SORT.field);
  const [sortDraftOrder, setSortDraftOrder] = useState(DEFAULT_SORT.order);

  // ---- more filters (draft = what the panel edits, applied = what API uses)
  const [moreDraft, setMoreDraft] = useState(EMPTY_MORE);
  const [moreApplied, setMoreApplied] = useState(EMPTY_MORE);

  const setDraftField = (field, value) => setMoreDraft((prev) => ({ ...prev, [field]: value }));
  const activeMoreCount = Object.values(moreApplied).filter((v) => v !== '').length;

  // ---- master data ----------------------------------------------------------
  const [departments, setDepartments] = useState([]);
  const [talentstatusList, setTalentstatusList] = useState([]);
  const [publishList, setPublishList] = useState(DEFAULT_PUBLISH);
  const [employmentTypeList, setEmploymentTypeList] = useState([]);
  const [workModeList, setWorkModeList] = useState([]);
  const [experienceRangeList, setExperienceRangeList] = useState([]);

  // ---- Hold / Close / Reopen / Sort modal state ----------------------------
  const [activeModal, setActiveModal] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [holdReason, setHoldReason] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  // ---------------------------------------------------------------------
  const toast = (msg, type = 'success') => {
    const t = document.createElement('div');
    t.className = 'og-toast';
    const color = type === 'warn' ? '#fbbf24' : type === 'error' ? '#f87171' : '#4ade80';
    const icon = type === 'warn' ? 'fa-triangle-exclamation' : type === 'error' ? 'fa-times-circle' : 'fa-check-circle';
    t.innerHTML = `<i class="fa-solid ${icon}" style="color:${color};"></i>${msg}`;
    document.body.appendChild(t);
    setTimeout(() => {
      t.style.opacity = '0';
      setTimeout(() => t.remove(), 300);
    }, 2800);
  };

  // ---------------------------------------------------------------------
  // master data loaders
  // ---------------------------------------------------------------------
  const loadMaster = async (masterType, setter) => {
    try {
      const response = await GetMasterTypeData({ masterType, allRecords: true, organisationId: userDetails?.OrgId });
      setter(response?.Data || []);
    } catch (error) {
      toster?.current?.error(ERROR_LIST?.MasterDataType);
    }
  };

  const getDepartments = () => loadMaster('Department', setDepartments);
  const getJobStatus = () => loadMaster('JobStatus', setTalentstatusList);
  const getEmploymentTypes = () => loadMaster('EmploymentType', setEmploymentTypeList);
  const getWorkModes = () => loadMaster('WorkMode', setWorkModeList);
  const getExperienceRange = () => loadMaster('ExperienceRange', setExperienceRangeList);

  // ---------------------------------------------------------------------
  // job list loader
  // ---------------------------------------------------------------------
  const fetchJobList = async () => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = {
        JobId: 0,
        Guid: '',
        TalentRequestId: 0,
        RecruiterEmpId: 0,
        JobStatusId: filterStatus || '',
        VisibilityId: '',
        EmploymentTypeId: moreApplied.employmentType || '',
        WorkModeId: moreApplied.workMode || '',
        DepartmentId: department || '',
        Location: moreApplied.location || '',
        Search: searchText,
        PageNumber: Math.floor(first / rows) + 1,
        PageSize: rows,
        PostedAfter: moreApplied.postedAfter || '',
        CreatedFrom: '',
        CreatedTo: '',
        SortBy: sortField,
        SortOrder: sortOrder,
        ExperienceRangeId: moreApplied.experienceRange || '',
        SalaryRangeId: '',
        IsPublished: Number(publishStatus) || 0,
      };

      const response = await getJobList(params);
      if (reqId !== requestIdRef.current) return; // a newer request superseded this one

      const data = response?.Data;
      setJobList(data?.Jobs || []);
      setTotalRecords(data?.TotalCount || 0);
      setSummary(data?.Summary || { TotalJobs: 0, OpenJobs: 0, OnHoldJobs: 0, ClosedJobs: 0 });
    } catch (error) {
      if (reqId !== requestIdRef.current) return;
      toster?.current?.error(ERROR_LIST?.TalentRequestList || 'Failed to load jobs');
      setJobList([]);
      setTotalRecords(0);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    getDepartments();
    getJobStatus();
    getEmploymentTypes();
    getWorkModes();
    getExperienceRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchJobList();
  }, [first, rows, filterStatus, department, publishStatus, searchText, sortField, sortOrder, moreApplied]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFirst(0);
      setSearchText(searchInput);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  const departmentBreakdown = useMemo(() => {
    const map = {};
    jobList.forEach((j) => {
      const name = j.DepartmentName || 'Others';
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [jobList]);

  const pipelineBreakdown = useMemo(() => {
    const map = {};
    jobList.forEach((j) => {
      (j.StageCounts || []).forEach((sc) => {
        map[sc.Stage] = (map[sc.Stage] || 0) + (sc.CandidateCount || 0);
      });
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [jobList]);

  const maxDeptCount = Math.max(1, ...departmentBreakdown.map(([, c]) => c), 0);
  const maxPipelineCount = Math.max(1, ...pipelineBreakdown.map(([, c]) => c), 0);

  const total = summary?.TotalJobs || 0;
  const openPct = total ? (summary.OpenJobs / total) * 100 : 0;
  const holdPct = total ? (summary.OnHoldJobs / total) * 100 : 0;
  const closedPct = total ? (summary.ClosedJobs / total) * 100 : 0;
  const CIRC = 2 * Math.PI * 32;
  const openLen = (openPct / 100) * CIRC;
  const holdLen = (holdPct / 100) * CIRC;
  const closedLen = (closedPct / 100) * CIRC;

  // ---------------------------------------------------------------------
  // filter / pagination handlers
  // ---------------------------------------------------------------------
  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const selectStatusPill = (id) => {
    setFirst(0);
    setFilterStatus(id);
  };

  const toggleMoreFilters = () => {
    if (!showMoreFilters) {
      // opening: sync draft with what is currently applied
      setMoreDraft(moreApplied);
    }
    setShowMoreFilters(!showMoreFilters);
  };

  const applyMoreFilters = () => {
    setFirst(0);
    setMoreApplied({ ...moreDraft, location: moreDraft.location.trim() });
    setShowMoreFilters(false);
  };

  const clearMoreFilters = () => {
    setMoreDraft(EMPTY_MORE);
    setMoreApplied(EMPTY_MORE);
    setFirst(0);
  };

  const copyJobLink = (job) => {
    const url = `${window.location.origin}/Recruitment/Job-Detail-Recruitment/${job.Id}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast('Link copied to clipboard'))
      .catch(() => toast('Could not copy link', 'error'));
  };

  // ---------------------------------------------------------------------
  // Hold / Close / Reopen / Sort modals
  // ---------------------------------------------------------------------
  const openModal = (modal, job = null) => {
    setActiveModal(modal);
    if (job) setActiveJob(job);
    if (modal === 'holdModal') setHoldReason('');
    if (modal === 'closeJobModal') setCloseReason('');
    if (modal === 'sortModal') {
      setSortDraftField(sortField);
      setSortDraftOrder(sortOrder);
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setActiveJob(null);
    setHoldReason('');
    setCloseReason('');
  };

  const getStatusIdByKey = (key) => {
    const found = talentstatusList.find((s) => statusKey(s.Name) === key);
    return found?.Id ?? '';
  };

  const submitStatusChange = async (modal, reason) => {
    if (!activeJob) return;
    const targetKey = TARGET_STATUS_KEY[modal];
    const statusId = getStatusIdByKey(targetKey);

    if (!statusId) {
      toast('Could not find a matching job status to apply', 'error');
      return;
    }

    setSubmittingAction(true);
    try {
      await postJobStatus({
        JobId: activeJob.Id,
        JobStatusId: statusId,
        Reason: reason || '',
      });
      toast(`${activeJob.JobCode || activeJob.Title} updated`);
      closeModal();
      fetchJobList();
    } catch (error) {
      toster?.current?.error(ERROR_LIST?.TalentRequestList || 'Failed to update job status');
    } finally {
      setSubmittingAction(false);
    }
  };

  const confirmHold = () => submitStatusChange('holdModal', holdReason);
  const confirmClose = () => submitStatusChange('closeJobModal', closeReason);
  const confirmReopen = () => submitStatusChange('reopenModal', '');

  const confirmSort = () => {
    setSortField(sortDraftField);
    setSortOrder(sortDraftOrder);
    setFirst(0);
    closeModal();
  };

  // ---------------------------------------------------------------------
  // render helpers
  // ---------------------------------------------------------------------
  const renderNA = () => <span style={naStyle}>{NA}</span>;

  const renderSkills = (job) => {
    const skills = job.Skills || [];
    if (skills.length === 0) return renderNA();
    return (
      <div className="jc-skills-inner">
        {skills.slice(0, 3).map((s) => (
          <span className="skill-tag" key={s.Id || s.Guid}>
            {s.SkillName}
          </span>
        ))}
        {skills.length > 3 && <span className="skill-more">+{skills.length - 3}</span>}
      </div>
    );
  };

  const renderJobCard = (job) => {
    const key = statusKey(job.JobStatus);
    return (
      <Link
        to={`/Recruitment/Job-Detail/${job?.Guid}`}
        key={job.Id}
        className={`job-card ${STATUS_TO_CARD_CLASS[key]}`}
        data-status={key}
        data-title={job.Title}
        data-dept={job.DepartmentName}
      >
        <div className="jc-top">
          <div className="jc-code-row">
            <span className="jc-code">{safe(job.JobCode)}</span>

            <div className="job-status-actions">
              <span className={`jc-age ${getAgeClass(job.JobAgeInDays)}`}>
                <i className="fa-solid fa-clock job-age-icon"></i>
                {job.JobAgeInDays !== undefined && job.JobAgeInDays !== null ? `${job.JobAgeInDays}d` : NA}
              </span>

              <span className={`status-badge ${STATUS_TO_BADGE_CLASS[key]}`}>{safe(job.JobStatus)}</span>
            </div>
          </div>

          <div className="jc-title">{safe(job.Title)}</div>

          <div className="jc-chips">
            <span className="jc-chip">
              <i className="fa-solid fa-building"></i>
              {safe(job.DepartmentName)}
            </span>

            <span className="jc-chip">
              <i className="fa-solid fa-location-dot"></i>
              {safe(job.Location)}
            </span>

            <span className="jc-chip">
              <i className="fa-regular fa-clock"></i>
              {safe(job.EmploymentType)}
            </span>

            <span className="jc-chip">
              <i className={workModeIcon(job.WorkMode)}></i>
              {safe(job.WorkMode)}
            </span>
          </div>
        </div>

        <div className="jc-mid">
          <div className="jc-meta">
            <i className="fa-solid fa-briefcase jc-meta-icon"></i>
            <strong>{safe(job.ExperienceRange)}</strong>
          </div>

          <div className="jc-meta">
            <i className="fa-solid fa-indian-rupee-sign jc-meta-icon"></i>
            <strong>{safe(job.SalaryRange)}</strong>
          </div>

          <div className="jc-meta">
            <i className={`fa-solid fa-circle-dot jc-meta-${key}-icon`}></i>
            <strong>{job.NumberOfOpenings ?? 0}</strong>&nbsp;openings
          </div>
        </div>

        <div className="jc-skills">{renderSkills(job)}</div>

        <div className="jc-foot">
          <div className="jc-foot-left">
            <div className="jc-cand">
              <i className="fa-solid fa-user-group"></i>
              {job.TotalCandidates ?? 0}
            </div>

            <div className="jc-posted">{formatDate(job.PublishedOn)}</div>

            <div className="jc-recruiter">
              <div className="jc-av">{getInitials(job.RecruiterName)}</div>
              <span className="jc-recruiter-name">{safe(job.RecruiterName)}</span>
            </div>
          </div>

          <div className="jc-actions">
            {key === 'closed' ? (
              <>
                <Link to={`/Recruitment/Job-Detail/${job?.Guid}`} className="btn-ghost btn-xs" title="View">
                  <i className="fa-solid fa-eye"></i>
                </Link>

                <button className="btn-ghost btn-xs reopen-action" onClick={() => openModal('reopenModal', job)}>
                  <i className="fa-solid fa-rotate-left"></i>
                  Reopen
                </button>
              </>
            ) : (
              <>
                <Link to={`/Recruitment/Job-Detail/${job?.Guid}`} className="btn-ghost btn-xs" title="View">
                  <i className="fa-solid fa-eye"></i>
                </Link>

                <Link to={`/Recruitment/Reimbursement-Job/Edit/${job.Id}`} className="btn-ghost btn-xs" title="Edit">
                  <i className="fa-solid fa-pen"></i>
                </Link>

                {key === 'hold' ? (
                  <button className="btn-ghost btn-xs reopen-action" title="Reopen" onClick={() => openModal('reopenModal', job)}>
                    <i className="fa-solid fa-play"></i>
                  </button>
                ) : (
                  <button className="btn-ghost btn-xs hold-action" title="Hold" onClick={() => openModal('holdModal', job)}>
                    <i className="fa-solid fa-pause"></i>
                  </button>
                )}

                <button className="btn-ghost btn-xs close-action" title="Close" onClick={() => openModal('closeJobModal', job)}>
                  <i className="fa-solid fa-xmark"></i>
                </button>

                <button className="btn-ghost btn-xs" title="Copy link" onClick={() => copyJobLink(job)}>
                  <i className="fa-solid fa-link"></i>
                </button>
              </>
            )}
          </div>
        </div>
      </Link>
    );
  };

  const renderJobRow = (job) => {
    const key = statusKey(job.JobStatus);
    return (
      <tr key={job.Id}>
        <td>
          <input type="checkbox" className="table-checkbox" />
        </td>

        <td>
          <div className="tbl-job-title">{safe(job.Title)}</div>
          <div className="tbl-job-code">{safe(job.JobCode)}</div>
        </td>

        <td>{safe(job.DepartmentName)}</td>
        <td>
          {safe(job.Location)} {job.Location && job.WorkMode ? '·' : ''} {safe(job.WorkMode)}
        </td>
        <td>{safe(job.ExperienceRange)}</td>
        <td>{job.NumberOfOpenings ?? 0}</td>
        <td className="table-candidates">{job.TotalCandidates ?? 0}</td>

        <td>
          <span className={`table-age ${key === 'hold' ? 'table-age-hold' : ''}`}>
            {job.JobAgeInDays !== undefined && job.JobAgeInDays !== null ? `${job.JobAgeInDays}d` : NA}
          </span>
        </td>

        <td>
          <span className={`status-badge ${STATUS_TO_BADGE_CLASS[key]}`}>{safe(job.JobStatus)}</span>
        </td>

        <td>
          <div className="table-recruiter">
            <div className="jc-av">{getInitials(job.RecruiterName)}</div>
            {safe(job.RecruiterName)}
          </div>
        </td>

        <td>
          <div className="table-actions">
            <Link to={`/Recruitment/Job-Detail/${job?.Guid}`} className="btn-ghost btn-xs" title="View">
              <i className="fa-solid fa-eye"></i>
            </Link>

            <Link to={`/Recruitment/Reimbursement-Job/Edit/${job.Id}`} className="btn-ghost btn-xs" title="Edit">
              <i className="fa-solid fa-pen"></i>
            </Link>

            {key === 'closed' ? (
              <button className="btn-ghost btn-xs reopen-action" title="Reopen" onClick={() => openModal('reopenModal', job)}>
                <i className="fa-solid fa-rotate-left"></i>
              </button>
            ) : key === 'hold' ? (
              <button className="btn-ghost btn-xs reopen-action" title="Reopen" onClick={() => openModal('reopenModal', job)}>
                <i className="fa-solid fa-play"></i>
              </button>
            ) : (
              <button className="btn-ghost btn-xs hold-action" title="Hold" onClick={() => openModal('holdModal', job)}>
                <i className="fa-solid fa-pause"></i>
              </button>
            )}

            <button className="btn-ghost btn-xs close-action" title="Close" onClick={() => openModal('closeJobModal', job)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="topbar">
        <i className="fa-solid fa-briefcase"></i>
        <div className="breadcrumb topbar-breadcrumb">
          <a href="#">Recruitment</a>
          <i className="fa-solid fa-chevron-right"></i>
          <strong className="topbar-current">Jobs</strong>
        </div>
        <div className="top-right">
          <div className="user-mini">
            <div className="top-avatar">{getInitials(userDetails?.Name)}</div>
            <div>
              <div className="user-name">{safe(userDetails?.Name)}</div>
              <div className="user-role">{safe(userDetails?.RoleName)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid position-relation">
        <div className="jobs-page">
          {/* ========================= PAGE HEADER ========================= */}
          <div className="page-hd">
            <div className="Page-head">
              <div className="leftside-head">
                <h2 className="mb-0">
                  <i className="fa-solid fa-briefcase jobs-title-icon"></i>
                  Jobs
                </h2>

                <div className="breadcrumb">Open positions, pipelines and candidate tracking</div>
              </div>

              <div className="jobs-page-hd-actions">
                <div className="view-toggle">
                  <button className={`vt-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')}>
                    <i className="fa-solid fa-table-cells-large"></i>
                    Cards
                  </button>

                  <button className={`vt-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>
                    <i className="fa-solid fa-table-list"></i>
                    Table
                  </button>
                </div>

                <Link to="/Recruitment/Reimbursement-Job/Create" className="btn btn-primary">
                  <i className="fa-solid fa-plus"></i>
                  Create Job
                </Link>
              </div>
            </div>
          </div>

          {/* ========================= SUMMARY ========================= */}
          <div className="summary-section">
            <div className="summary-toggle" onClick={() => setShowSummary((v) => !v)} style={{ cursor: 'pointer' }}>
              <h5>
                <i className="fa-solid fa-chart-pie summary-icon"></i>
                Summary &amp; Overview
              </h5>

              <div className="summary-toggle-right">
                <span className="summary-total">{totalRecords} total jobs</span>
                <i className={`fa-solid fa-chevron-${showSummary ? 'up' : 'down'} summary-chevron`}></i>
              </div>
            </div>

            {showSummary && (
              <div className="summary-body" id="summary-body">
                <div className="stat-strip">
                  <div className="stat-cell">
                    <div className="stat-lbl">Total Jobs</div>
                    <div className="stat-val stat-total">{summary?.TotalJobs ?? 0}</div>
                    <div className="stat-sub">All departments</div>
                  </div>

                  <div className="stat-cell">
                    <div className="stat-lbl">Open</div>
                    <div className="stat-val stat-open">{summary?.OpenJobs ?? 0}</div>
                    <div className="stat-sub">Actively hiring</div>
                  </div>

                  <div className="stat-cell">
                    <div className="stat-lbl">On Hold</div>
                    <div className="stat-val stat-hold">{summary?.OnHoldJobs ?? 0}</div>
                    <div className="stat-sub">Temporarily paused</div>
                  </div>

                  <div className="stat-cell">
                    <div className="stat-lbl">Closed</div>
                    <div className="stat-val stat-closed">{summary?.ClosedJobs ?? 0}</div>
                    <div className="stat-sub">Filled or cancelled</div>
                  </div>
                </div>

                <div className="charts-row">
                  {/* Status Donut */}
                  <div className="chart-cell">
                    <div className="chart-title">By Status</div>

                    <div className="status-chart-wrap">
                      <svg width="90" height="90" viewBox="0 0 90 90">
                        <circle cx="45" cy="45" r="32" fill="none" stroke="#f3f4f6" strokeWidth="14" />
                        <circle
                          cx="45"
                          cy="45"
                          r="32"
                          fill="none"
                          stroke="#16a34a"
                          strokeWidth="14"
                          strokeDasharray={`${openLen} ${CIRC - openLen}`}
                          strokeDashoffset="0"
                          transform="rotate(-90 45 45)"
                        />
                        <circle
                          cx="45"
                          cy="45"
                          r="32"
                          fill="none"
                          stroke="#d97706"
                          strokeWidth="14"
                          strokeDasharray={`${holdLen} ${CIRC - holdLen}`}
                          strokeDashoffset={-openLen}
                          transform="rotate(-90 45 45)"
                        />
                        <circle
                          cx="45"
                          cy="45"
                          r="32"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="14"
                          strokeDasharray={`${closedLen} ${CIRC - closedLen}`}
                          strokeDashoffset={-(openLen + holdLen)}
                          transform="rotate(-90 45 45)"
                        />
                        <text x="45" y="42" textAnchor="middle" className="chart-svg-text chart-svg-number">
                          {summary?.TotalJobs ?? 0}
                        </text>
                        <text x="45" y="55" textAnchor="middle" className="chart-svg-text chart-svg-label">
                          Jobs
                        </text>
                      </svg>

                      <div>
                        <div className="cl-item chart-legend-item">
                          <span className="cl-dot legend-open"></span>
                          Open ({summary?.OpenJobs ?? 0})
                        </div>
                        <div className="cl-item chart-legend-item">
                          <span className="cl-dot legend-hold"></span>
                          Hold ({summary?.OnHoldJobs ?? 0})
                        </div>
                        <div className="cl-item">
                          <span className="cl-dot legend-closed"></span>
                          Closed ({summary?.ClosedJobs ?? 0})
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Department (from jobs on this page) */}
                  <div className="chart-cell">
                    <div className="chart-title">By Department</div>
                    {departmentBreakdown.length === 0 && renderNA()}
                    {departmentBreakdown.map(([name, count]) => (
                      <div className="dept-bar-row" key={name}>
                        <span className="dept-label">{name}</span>
                        <div className="dept-bar-track">
                          <div className="dept-bar-fill" style={{ width: `${(count / maxDeptCount) * 100}%` }}></div>
                        </div>
                        <span className="dept-count">{count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Candidate Pipeline (from jobs on this page) */}
                  <div className="chart-cell">
                    <div className="chart-title">Candidate Pipeline</div>
                    {pipelineBreakdown.length === 0 && renderNA()}
                    {pipelineBreakdown.map(([stage, count]) => (
                      <div className="funnel-row" key={stage}>
                        <span className="funnel-stage">{stage}</span>
                        <div className="funnel-bar-track">
                          <div className="funnel-bar-fill" style={{ width: `${(count / maxPipelineCount) * 100}%` }}></div>
                        </div>
                        <span className="funnel-num">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ========================= FILTER BAR ========================= */}
          <div className="filter-bar">
            <div className="filter-row">
              <div className="search-wrap">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  className="search-input"
                  id="searchInput"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search job title, code, skills…"
                />
              </div>

              <div className="filter-divider"></div>

              <div className="status-pills">
                <button className={`sp-bx sall ${filterStatus === '' ? 'active' : ''}`} onClick={() => selectStatusPill('')}>
                  All ({summary?.TotalJobs ?? 0})
                </button>

                {talentstatusList.map((s) => {
                  const key = statusKey(s.Name);
                  const count = key === 'open' ? summary?.OpenJobs : key === 'hold' ? summary?.OnHoldJobs : summary?.ClosedJobs;
                  return (
                    <button
                      key={s.Id}
                      className={`sp-bx ${STATUS_TO_PILL_CLASS[key]} ${filterStatus === String(s.Id) ? 'active' : ''}`}
                      onClick={() => selectStatusPill(String(s.Id))}
                    >
                      {s.Name} ({count ?? 0})
                    </button>
                  );
                })}
              </div>

              <div className="filter-divider"></div>

              <select
                className="og-select"
                id="deptFilter"
                value={department}
                onChange={(e) => {
                  setFirst(0);
                  setDepartment(e.target.value);
                }}
              >
                <option value="">All Departments</option>
                {departments.map((item) => (
                  <option key={item?.Id} value={item?.Id}>
                    {item?.Name}
                  </option>
                ))}
              </select>

              <select
                className="og-select"
                id="statustFilter"
                value={publishStatus}
                onChange={(e) => {
                  setFirst(0);               
                  setPublishStatus(e.target.value);
                }}
              >
                {publishList.map((item) => (
                  <option key={item.Id} value={String(item.Id)}>
                    {item.Name}
                  </option>
                ))}
              </select>

              <button className="btn btn-outline btn-sm" id="moreBtn" onClick={toggleMoreFilters}>
                <i className="fa-solid fa-sliders"></i>
                More Filters{activeMoreCount > 0 ? ` (${activeMoreCount})` : ''}
              </button>

              <button className="btn sort-btn" onClick={() => openModal('sortModal')}>
                <i className={`fa-solid ${sortOrder === 'ASC' ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short'}`}></i>
                Sort: {SORT_FIELDS[sortField]} ({sortOrder === 'ASC' ? 'Asc' : 'Desc'})
              </button>
            </div>

            {/* More Filters */}
            {showMoreFilters && (
              <div className="more-panel open" id="morePanel">
                <div className="mp-g">
                  <label>Location</label>
                  <input
                    className="mp-input"
                    placeholder="City or Remote"
                    value={moreDraft.location}
                    onChange={(e) => setDraftField('location', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyMoreFilters()}
                  />
                </div>

                <div className="mp-g">
                  <label>Job Type</label>
                  <select
                    className="mp-input og-select more-select"
                    value={moreDraft.employmentType}
                    onChange={(e) => setDraftField('employmentType', e.target.value)}
                  >
                    <option value="">Any type</option>
                    {employmentTypeList.map((item) => (
                      <option key={item?.Id} value={String(item?.Id)}>
                        {item?.Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mp-g">
                  <label>Work Mode</label>
                  <select
                    className="mp-input og-select more-select"
                    value={moreDraft.workMode}
                    onChange={(e) => setDraftField('workMode', e.target.value)}
                  >
                    <option value="">Any mode</option>
                    {workModeList.map((item) => (
                      <option key={item?.Id} value={String(item?.Id)}>
                        {item?.Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mp-g">
                  <label>Experience (yrs)</label>
                  <select
                    className="mp-input og-select more-select"
                    value={moreDraft.experienceRange}
                    onChange={(e) => setDraftField('experienceRange', e.target.value)}
                  >
                    <option value="">Any range</option>
                    {experienceRangeList.map((item) => (
                      <option key={item?.Id} value={String(item?.Id)}>
                        {item?.Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mp-g">
                  <label>Posted after</label>
                  <input
                    type="date"
                    className="mp-input"
                    value={moreDraft.postedAfter}
                    onChange={(e) => setDraftField('postedAfter', e.target.value)}
                  />
                </div>

                <div className="mp-g more-action">
                  <button className="btn btn-primary btn-sm apply-filter-btn" onClick={applyMoreFilters}>
                    Apply
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={clearMoreFilters}>
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ========================= LOADING / EMPTY ========================= */}
          {loading && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>Loading jobs…</div>}

          {!loading && jobList.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>No jobs found for the current filters.</div>
          )}

          {/* ========================= CARDS VIEW ========================= */}
          {!loading && jobList.length > 0 && viewMode === 'cards' && (
            <div id="cardsView">
              <div className="jobs-grid" id="jobsGrid">
                {jobList.map(renderJobCard)}
              </div>
            </div>
          )}

          {/* ========================= TABLE VIEW ========================= */}
          {!loading && jobList.length > 0 && viewMode === 'table' && (
            <div id="tableView" className="table-view">
              <div className="jobs-table-wrap">
                <table className="jobs-table">
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox" className="table-checkbox" />
                      </th>
                      <th>Job</th>
                      <th>Department</th>
                      <th>Location / Mode</th>
                      <th>Experience</th>
                      <th>Openings</th>
                      <th>Candidates</th>
                      <th>Age</th>
                      <th>Status</th>
                      <th>Recruiter</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{jobList.map(renderJobRow)}</tbody>
                </table>
              </div>
            </div>
          )}

          {totalRecords > 0 && (
            <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[10, 25, 50, 100]} onPageChange={onPageChange} />
          )}
        </div>
      </div>

      {/* ==================== Hold Modal ==================== */}
      {activeModal === 'holdModal' && activeJob && (
        <div className="modal-overlay open" id="holdModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon hold-modal-icon">
                <i className="fa-solid fa-pause"></i>
              </div>

              <div className="job-modal-title-wrap">
                <div className="modal-title">Put job on hold?</div>
                <div className="modal-sub">
                  {safe(activeJob.JobCode)} · {safe(activeJob.Title)}
                </div>
              </div>

              <button className="modal-close-btn" onClick={closeModal}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="job-modal-description">
                The job will be paused and removed from active listings. Existing candidates will not be affected. You can reopen it anytime.
              </p>

              <div>
                <label className="job-modal-label">
                  Reason for hold <span className="job-modal-optional">(optional)</span>
                </label>

                <textarea
                  className="og-textarea"
                  placeholder="e.g. Budget freeze, headcount review…"
                  rows="3"
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn btn-outline btn-sm" onClick={closeModal} disabled={submittingAction}>
                Cancel
              </button>

              <button className="btn btn-primary btn-sm" onClick={confirmHold} disabled={submittingAction}>
                <i className="fa-solid fa-pause"></i>
                {submittingAction ? 'Please wait…' : 'Put on Hold'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Close Job Modal ==================== */}
      {activeModal === 'closeJobModal' && activeJob && (
        <div className="modal-overlay open" id="closeJobModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon close-job-modal-icon">
                <i className="fa-solid fa-xmark"></i>
              </div>

              <div className="job-modal-title-wrap">
                <div className="modal-title">Close job?</div>
                <div className="modal-sub">
                  {safe(activeJob.JobCode)} · {safe(activeJob.Title)}
                </div>
              </div>

              <button className="modal-close-btn" onClick={closeModal}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="job-modal-description">
                Closing this job will stop accepting new applications. Active candidates in the pipeline will remain. This action cannot be
                easily undone — use "On Hold" if you want to temporarily pause.
              </p>

              <div>
                <label className="job-modal-label">
                  Reason for closing <span className="job-modal-optional">(optional)</span>
                </label>

                <select className="og-input close-job-select" value={closeReason} onChange={(e) => setCloseReason(e.target.value)}>
                  <option value="">Select reason…</option>
                  <option value="Position filled">Position filled</option>
                  <option value="Hiring paused">Hiring paused</option>
                  <option value="Budget cancelled">Budget cancelled</option>
                  <option value="Role no longer required">Role no longer required</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn-outline btn btn-sm" onClick={closeModal} disabled={submittingAction}>
                Cancel
              </button>

              <button className="btn btn-danger btn-sm" onClick={confirmClose} disabled={submittingAction}>
                <i className="fa-solid fa-xmark"></i>
                {submittingAction ? 'Please wait…' : 'Close Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Reopen Modal ==================== */}
      {activeModal === 'reopenModal' && activeJob && (
        <div className="modal-overlay open" id="reopenModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon reopen-modal-icon">
                <i className="fa-solid fa-rotate-left"></i>
              </div>

              <div className="job-modal-title-wrap">
                <div className="modal-title">Reopen job?</div>
                <div className="modal-sub">
                  {safe(activeJob.JobCode)} · {safe(activeJob.Title)}
                </div>
              </div>

              <button className="modal-close-btn" onClick={closeModal}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="job-modal-description reopen-description">
                This job will be set back to <strong>Open</strong> and will resume accepting applications if it was listed externally.
              </p>
            </div>

            <div className="modal-foot">
              <button className="btn btn-outline btn-sm" onClick={closeModal} disabled={submittingAction}>
                Cancel
              </button>

              <button className="btn btn-success btn-sm" onClick={confirmReopen} disabled={submittingAction}>
                <i className="fa-solid fa-play"></i>
                {submittingAction ? 'Please wait…' : 'Reopen Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Sort Modal ==================== */}
      {activeModal === 'sortModal' && (
        <div className="modal-overlay open" id="sortModal">
          <div className="modal-box">
            <div className="modal-head">
              <div className="modal-icon sort-modal-icon">
                <i className="fa-solid fa-arrow-down-wide-short"></i>
              </div>

              <div className="job-modal-title-wrap">
                <div className="modal-title">Sort Jobs</div>
              </div>

              <button className="modal-close-btn" onClick={closeModal}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="sort-field-group">
                <label className="job-modal-label">Sort by</label>
                <select
                  className="og-input sort-field-select"
                  value={sortDraftField}
                  onChange={(e) => setSortDraftField(e.target.value)}
                >
                  {Object.entries(SORT_FIELDS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sort-field-group">
                <label className="job-modal-label">Order</label>
                <div className="sort-order-toggle">
                  <button
                    type="button"
                    className={`sort-order-btn ${sortDraftOrder === 'ASC' ? 'active' : ''}`}
                    onClick={() => setSortDraftOrder('ASC')}
                  >
                    <i className="fa-solid fa-arrow-up-short-wide"></i>
                    Ascending
                  </button>

                  <button
                    type="button"
                    className={`sort-order-btn ${sortDraftOrder === 'DESC' ? 'active' : ''}`}
                    onClick={() => setSortDraftOrder('DESC')}
                  >
                    <i className="fa-solid fa-arrow-down-wide-short"></i>
                    Descending
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn btn-outline btn-sm" onClick={closeModal}>
                Cancel
              </button>

              <button className="btn btn-primary btn-sm" onClick={confirmSort}>
                Apply Sort
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JobsPage;